import React, { useState, useEffect } from 'react'
import { Box, Typography, CircularProgress } from '@mui/material'
import { Radio } from 'lucide-react'
import {
  useGetListeningTopicFeedLazyQuery,
  useMarkTopicContentInteractedMutation,
  useHideTopicContentMutation,
} from 'graphql/generated/graphql'
import ContentFeed from 'pages/DiscoverContent/components/ContentFeed'

interface TopicColumnProps {
  topicId: string
  topicName: string
}

/**
 * A single vertical column showing the feed for one topic (all platforms).
 * Used in the multi-topic side-by-side feeds view.
 */
export default function TopicColumn({ topicId, topicName }: TopicColumnProps): JSX.Element {
  const [content, setContent] = useState<any[]>([])
  const [fetchFeed, { loading }] = useGetListeningTopicFeedLazyQuery()
  const [markInteracted] = useMarkTopicContentInteractedMutation()
  const [hideContent] = useHideTopicContentMutation()

  const loadContent = async (): Promise<void> => {
    const result = await fetchFeed({
      variables: {
        input: {
          topicId,
          limit: 50,
          offset: 0,
        },
      },
      fetchPolicy: 'network-only',
    })
    if (result.data?.getListeningTopicFeed) {
      setContent(result.data.getListeningTopicFeed.content)
    }
  }

  useEffect(() => {
    loadContent().catch(console.error)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicId])

  const handleInteract = (contentId: string): void => {
    setContent((prev) => {
      const updated = prev.map((c) =>
        c.id === contentId ? { ...c, interactedWith: true } : c
      )
      return [...updated].sort((a, b) => {
        if (a.interactedWith !== b.interactedWith) return a.interactedWith ? 1 : -1
        return (b.similarity ?? 0) - (a.similarity ?? 0)
      })
    })
    markInteracted({ variables: { contentId } }).catch(console.error)
  }

  const handleHide = (contentId: string): void => {
    setContent((prev) => prev.filter((c) => c.id !== contentId))
    hideContent({ variables: { contentId } }).catch(console.error)
  }

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 320,
        maxWidth: 480,
        height: 'calc(100vh - 220px)',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      {/* Fixed column header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          background: 'rgba(255,255,255,0.04)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            boxShadow: '0 2px 8px rgba(168, 85, 247, 0.3)',
          }}
        >
          <Radio size={16} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: '14px',
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {topicName}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', lineHeight: 1.2 }}>
            {content.length} {content.length === 1 ? 'post' : 'posts'}
          </Typography>
        </Box>
      </Box>

      {/* Scrollable body */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(168, 85, 247, 0.3)',
            borderRadius: '4px',
          },
        }}
      >
        {loading && content.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: '#a855f7' }} />
          </Box>
        ) : content.length === 0 ? (
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', textAlign: 'center', py: 4 }}>
            No content yet
          </Typography>
        ) : (
          <Box sx={{ '& > div': { columnCount: '1 !important' } }}>
            <ContentFeed content={content} onInteract={handleInteract} onHide={handleHide} />
          </Box>
        )}
      </Box>
    </Box>
  )
}
